import type { FaireReviewEntity } from "@faire/playground-entity-db/entity_types/review";
import { getEntityByToken } from "@faire/prototype-playground/data/get_entity";
import { cn } from "@faire/prototype-playground/lib/utils";
import { tsv } from "@faire/slate/lib/tsv";
import React, { useState } from "react";

import { PADX } from "../constants";
import { SectionHeader } from "../SectionHeader";

/** Form factor for the featured-review module.
 * - `cards`  — the familiar shoppable review-card row (one card per review).
 * - `quote`  — one full-width pull-quote at a time, purchased products beneath.
 * - `image`  — same skeleton as `quote`, but a large brand image carries the
 *              top band and the quote sits over it (reads like a collection).
 */
export type FeaturedReviewsLayout = "cards" | "quote" | "image";

interface FeaturedReviewsProps {
  /** Brand-curated reviews, already ordered by the brand. */
  reviews: FaireReviewEntity[];
  title?: string;
  /** How many of the curated reviews to show. Defaults to all of them. */
  maxCount?: number;
  layout?: FeaturedReviewsLayout;
  /** Large image for the `image` layout. Falls back to the first purchased
   * product's photo when the brand hasn't supplied a lifestyle image. */
  imageUrl?: string;
  /** Show what the reviewer bought beneath the quote. Defaults on. */
  showPurchased?: boolean;
  /** Clicking a purchased-product thumbnail (routes to that product's PDP in
   * production). Omit to render the thumbnails as static. */
  onProductClick?: (productToken: string) => void;
  /** "See all reviews" in the header — the module's link off to Faire, where
   * the full ratings-and-reviews view lives. */
  onSeeAll?: () => void;
  seeAllLabel?: string;
}

interface PurchasedProduct {
  token: string;
  name: string;
  imageUrl?: string;
}

/**
 * "Featured reviews" — the reviews a brand has chosen to lead with, rather
 * than an algorithmically selected quote. Three form factors (see
 * `FeaturedReviewsLayout`) over the same brand-curated data.
 *
 * Purchased products render as image-only tiles: no name, no price. The point
 * of this module is proof, not merchandising — the tiles say "this buyer
 * stocked four of the five" at a glance, and the hover overlay is the way
 * into an individual PDP for anyone who wants it.
 */
export const FeaturedReviews: React.FC<FeaturedReviewsProps> = ({
  reviews,
  title = "Featured reviews",
  maxCount,
  layout = "cards",
  imageUrl,
  showPurchased = true,
  onProductClick,
  onSeeAll,
  seeAllLabel = "See all reviews",
}) => {
  const shown = reviews.slice(0, maxCount ?? reviews.length);
  const [activeIndex, setActiveIndex] = useState(0);

  if (shown.length === 0) {
    return null;
  }

  // The cycling layouts show one review at a time; clamp in case `maxCount`
  // shrank the set below the current index (the count knob does exactly this).
  const safeIndex = Math.min(activeIndex, shown.length - 1);

  if (layout === "cards") {
    return (
      <div className={cn("pt-8 pb-16", PADX)}>
        <SectionHeader
          title={title}
          showArrows={false}
          showSeeAll={onSeeAll !== undefined}
          seeAllLabel={seeAllLabel}
          onSeeAll={onSeeAll}
        />
        <div className="grid gap-[var(--grid-gutter,16px)] sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((review) => (
            <ShoppableReviewCard
              key={review.token}
              review={review}
              onProductClick={onProductClick}
            />
          ))}
        </div>
      </div>
    );
  }

  const goPrev = () =>
    setActiveIndex((safeIndex - 1 + shown.length) % shown.length);
  const goNext = () => setActiveIndex((safeIndex + 1) % shown.length);

  return (
    <div className={cn("pt-8 pb-16", PADX)}>
      {/* Standard framework carousel controls (Slate chevron IconButtons in the
          section header) — same affordance as every other row on the page. */}
      <SectionHeader
        title={title}
        showArrows={shown.length > 1}
        showSeeAll={onSeeAll !== undefined}
        seeAllLabel={seeAllLabel}
        onSeeAll={onSeeAll}
        onPrev={goPrev}
        onNext={goNext}
      />
      {/* One continuous brand-tinted field: the quote and the products it sold
          are the same statement, so they share a surface instead of the
          products sitting on a separate white tray below the card.

          Every curated review is rendered into the SAME grid cell, with the
          inactive ones hidden. The card therefore sizes itself to its tallest
          member once and never resizes while cycling — a three-word quote and a
          five-line one occupy identical space. A fixed pixel height can't do
          this job: the tallest member changes with the 3/4/5 count knob and
          with whatever reviews a brand curates. */}
      <div className={cardStyle.cls({ layout })}>
        {shown.map((review, index) => {
          const isActive = index === safeIndex;
          return (
            <div
              key={review.token}
              aria-hidden={!isActive}
              className={cn(
                "col-start-1 row-start-1 flex flex-col",
                !isActive && "invisible"
              )}
            >
              {layout === "image" ? (
                <ImageQuoteBand review={review} imageUrl={imageUrl} />
              ) : (
                <PlainQuoteBand review={review} withPurchased={showPurchased} />
              )}
              {showPurchased ? (
                <PurchasedStrip
                  review={review}
                  onProductClick={isActive ? onProductClick : undefined}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// A single-cell grid so all reviews stack on top of each other and the card
// takes the height of the tallest. The image layout's own band carries its
// min-height, so no per-layout height is needed here.
const cardStyle = tsv({
  base: [
    "border-fs-border-muted grid grid-cols-1 grid-rows-1 overflow-hidden",
    "rounded-lg border bg-[var(--brand-secondary)]",
  ],
  variants: {
    layout: { cards: "", quote: "", image: "" },
  },
});

const STAR_PATH =
  "m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z";

const Star: React.FC<{ filled?: boolean; onDark?: boolean }> = ({
  filled = true,
  onDark = false,
}) => {
  const emptyFill = onDark ? "rgba(255,255,255,0.35)" : "#e5e5e5";
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? (onDark ? "#ffffff" : "#333333") : emptyFill}
      aria-hidden
    >
      <path d={STAR_PATH} />
    </svg>
  );
};

const resolvePurchased = (review: FaireReviewEntity): PurchasedProduct[] =>
  (review.purchasedProductTokens ?? [])
    .map((token) => getEntityByToken(token, "product"))
    .filter(
      (product): product is NonNullable<typeof product> => product !== undefined
    )
    .map((product) => ({
      token: product.token,
      name: product.name,
      imageUrl: product.imageUrl,
    }));

const Attribution: React.FC<{
  review: FaireReviewEntity;
  onDark?: boolean;
}> = ({ review, onDark = false }) => (
  <span
    className={cn(
      "type-fs-label",
      onDark ? "text-fs-text-on-dark" : "text-fs-text-subdued"
    )}
  >
    {review.reviewerName}
    {review.reviewerLocation ? ` · ${review.reviewerLocation}` : ""}
  </span>
);

/** `quote` layout's top band: a large pull-quote on the brand's light surface. */
const PlainQuoteBand: React.FC<{
  review: FaireReviewEntity;
  withPurchased: boolean;
}> = ({ review, withPurchased }) => (
  <div
    className={cn(
      "flex flex-1 flex-col items-center justify-center gap-4 text-center",
      "px-6 pt-12 md:px-16 md:pt-16",
      // Without a product strip under it the quote is the whole card, so it
      // has to close the box itself rather than leave the strip's gap behind.
      withPurchased ? "pb-8" : "pb-12 md:pb-16"
    )}
  >
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} filled={star <= review.stars} />
      ))}
    </div>
    <p className="type-fs-display-s text-fs-text-primary max-w-3xl">
      &ldquo;{review.reviewText}&rdquo;
    </p>
    <Attribution review={review} />
  </div>
);

/** `image` layout's top band: brand image with the quote laid over a scrim. */
const ImageQuoteBand: React.FC<{
  review: FaireReviewEntity;
  imageUrl?: string;
}> = ({ review, imageUrl }) => {
  const purchased = resolvePurchased(review);
  const resolvedImage = imageUrl ?? purchased[0]?.imageUrl;

  return (
    <div className="relative min-h-[320px] md:min-h-[420px]">
      {resolvedImage ? (
        <img
          src={resolvedImage}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--brand-primary)]" />
      )}
      {/* Scrim: the quote has to stay legible over an arbitrary brand photo. */}
      <div className="absolute inset-0 bg-(--slate-color-grey-1000)/45" />
      <div className="relative flex min-h-[320px] flex-col items-center justify-center gap-4 px-6 py-12 text-center md:min-h-[420px] md:px-16">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} filled={star <= review.stars} onDark />
          ))}
        </div>
        <p className="type-fs-display-m text-fs-text-on-dark max-w-3xl">
          &ldquo;{review.reviewText}&rdquo;
        </p>
        <Attribution review={review} onDark />
      </div>
    </div>
  );
};

/** The purchased-product strip under a cycling quote — image-only tiles.
 *
 * Deliberately NOT on its own surface: it shares the quote band's background
 * so the module reads as one field rather than a quote card stacked on a
 * separate white tray. Centered to sit under the quote's own axis. */
const PurchasedStrip: React.FC<{
  review: FaireReviewEntity;
  onProductClick?: (productToken: string) => void;
}> = ({ review, onProductClick }) => {
  const purchased = resolvePurchased(review);

  if (purchased.length === 0) {
    return null;
  }

  // No "Purchased N products" label — the bags sitting under a retailer's quote
  // already read as what they bought.
  //
  // Scattered rather than a centred row of equal squares (Ryan, 2026-08-18).
  // A uniform strip reads as a spec sheet under an editorial quote, and with a
  // single purchased product it read as one lonely square. Sizes, vertical
  // offsets and small rotations come from a fixed table keyed by position, so
  // the arrangement is deterministic — it must not reshuffle on every render,
  // and the framework has no place for Math.random in a layout.
  //
  // The first tile stays square-on and largest: a review with one product still
  // has to look composed, not like a failed scatter.
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-6 pt-2 pb-14 md:gap-x-8 md:px-16 md:pb-20">
      {purchased.map((product, index) => {
        const scatter = SCATTER[index % SCATTER.length];
        return (
          <div
            key={product.token}
            // Inline because the values are data, not a class each: Tailwind
            // can only see class literals, so a rotate-[Ndeg] per tile would
            // have to be enumerated up front.
            style={{
              transform: `translateY(${scatter.offsetY}px) rotate(${scatter.rotate}deg)`,
            }}
          >
            <ProductThumb
              product={product}
              size={scatter.size}
              onProductClick={onProductClick}
            />
          </div>
        );
      })}
    </div>
  );
};

/** Per-position size, drop and tilt for the scattered purchased tiles. */
const SCATTER: {
  size: "large" | "medium" | "small";
  offsetY: number;
  rotate: number;
}[] = [
  { size: "large", offsetY: 0, rotate: 0 },
  { size: "small", offsetY: 16, rotate: -5 },
  { size: "medium", offsetY: -12, rotate: 4 },
  { size: "small", offsetY: 10, rotate: 6 },
  { size: "medium", offsetY: -8, rotate: -3 },
];

const thumbStyle = tsv({
  base: [
    "group bg-fs-surface-secondary relative overflow-hidden rounded-sm",
    "flex-shrink-0",
    // A PERMANENT white border, not a hover state (Ryan, 2026-08-18). These
    // brands shoot on cream, and the module's own surface is the brand's cream,
    // so an unbordered tile dissolves into its background — the photo had no
    // edge to read against. The white mount gives it one, and reads like a
    // print laid on the page rather than a UI square. `ring` draws outside the
    // box so it never crops the photo.
    "ring-4 ring-(--slate-color-grey-100)",
    // Hover lifts the print instead of ringing it, since the ring is now the
    // resting state. Keyboard focus gets the same, so it isn't mouse-only.
    "transition-transform hover:scale-[1.04] focus-visible:scale-[1.04]",
  ],
  variants: {
    interactive: { true: "cursor-pointer", false: "cursor-default" },
    // Enumerated rather than computed: Tailwind only sees class literals.
    size: {
      large: "h-28 w-28 md:h-36 md:w-36",
      medium: "h-20 w-20 md:h-24 md:w-24",
      small: "h-16 w-16 md:h-20 md:w-20",
    },
  },
});

/**
 * A purchased product as an image-only tile — deliberately no name and no
 * price. The hover overlay is the only affordance into the individual PDP.
 */
const ProductThumb: React.FC<{
  product: PurchasedProduct;
  size?: "large" | "medium" | "small";
  onProductClick?: (productToken: string) => void;
}> = ({ product, size = "medium", onProductClick }) => (
  <button
    type="button"
    title={product.name}
    aria-label={product.name}
    data-signup-gate
    onClick={() => onProductClick?.(product.token)}
    className={thumbStyle.cls({
      interactive: onProductClick !== undefined,
      size,
    })}
  >
    {product.imageUrl ? (
      <img
        src={product.imageUrl}
        alt={product.name}
        className="h-full w-full object-cover"
      />
    ) : null}
    {/* 5% — just enough to register as a state change. The "View" label this
        used to hold is gone with the heavy scrim that made it legible: at this
        opacity white text disappears on a pale product shot and dark text
        disappears on a black one. The ring is the affordance now, and the
        button keeps its title + aria-label, so nothing was lost for a screen
        reader or a hover tooltip. */}
    <span className="absolute inset-0 bg-(--slate-color-grey-1000)/5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
  </button>
);

/** `cards` layout: the existing shoppable review card, kept as one option. */
const ShoppableReviewCard: React.FC<{
  review: FaireReviewEntity;
  onProductClick?: (productToken: string) => void;
}> = ({ review, onProductClick }) => {
  const purchased = resolvePurchased(review);

  return (
    <div className="border-fs-border-muted flex min-w-0 flex-col gap-3 rounded-md border p-4 md:p-6">
      <div className="flex items-start justify-between">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} filled={star <= review.stars} />
          ))}
        </div>
        <span className="type-fs-label text-fs-text-subdued text-right">
          {review.reviewDate}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <p className="type-fs-paragraph text-fs-text-primary">
          {review.reviewText}
        </p>
        <p className="type-fs-label text-fs-text-subdued">
          {review.reviewerName}
          {review.reviewerLocation ? ` · ${review.reviewerLocation}` : ""}
        </p>
      </div>
      {purchased.length > 0 ? (
        <div className="mt-auto flex flex-col gap-2 pt-2">
          <span className="type-fs-label text-fs-text-subdued">Purchased</span>
          <div className="flex flex-wrap gap-2.5">
            {purchased.map((product) => (
              <ProductThumb
                key={product.token}
                product={product}
                onProductClick={onProductClick}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
